import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

const { API_URLBLAZE } = environment;

export interface AuditReason {
  reasonCode: string;
  description: string;
}

export interface PiiAuditRequest {
  userId: string;
  fieldName: string;
  reasonId: string;
  reasonDescription?: string; // Human-readable reason description
  comment: string;
  accessedBy: string;
  timestamp: string;
  context?: string; // 'field' for form inputs, 'cell' for grid cells
  
  // Enhanced entity context for audit logging with transaction support
  entityType?: string;
  entityMobile?: string;
  transactionId?: string; // Transaction ID for transaction-related PII
  referenceId?: string; // Generic reference ID (transaction, order, case, etc.)
}

export interface PiiAuditResponse {
  success: boolean;
  message: string;
  auditId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PiiAuditService {

  constructor(private http: HttpClient, private storageService: StorageService) { }

  /**
   * Get list of audit reasons from API based on user's role
   * @returns Observable<AuditReason[]>
   */
  getAuditReasons(): Observable<AuditReason[]> {
    try {
      // Get current user's role ID
      const currentUser = this.storageService.getUser();
      const roleId = currentUser?.roleId || currentUser?.ROLEID || 5; // Default to role 5 if not found
      
      // Use your actual API endpoint
      const url = `${environment.API_URLBLAZE}/user/get-reasons-by-role?roleId=${roleId}`;
      
      return this.http.get<AuditReason[]>(url).pipe(
        catchError(this.handleError<AuditReason[]>('getAuditReasons', this.getFallbackReasons()))
      );
    } catch (error) {
      console.error('Error getting user role, using fallback reasons:', error);
      return of(this.getFallbackReasons());
    }
  }

  /**
   * Fallback reasons in case API fails
   */
  private getFallbackReasons(): AuditReason[] {
    return [
      { reasonCode: 'RC001', description: 'Customer Identity Verification' },
      { reasonCode: 'RC002', description: 'Transaction Dispute Resolution' },
      { reasonCode: 'RC012', description: 'Audit Review' },
      { reasonCode: 'RC039', description: 'Account Login or Access Issue Resolution' }
    ];
  }

  /**
   * Log PII access audit
   * @param auditRequest - The audit request data
   * @returns Observable<PiiAuditResponse>
   */
  logPiiAccess(auditRequest: PiiAuditRequest): Observable<PiiAuditResponse> {
    // Log the audit request to console for debugging
    console.log('🔍 PII Audit Log:', {
      timestamp: new Date().toISOString(),
      userId: auditRequest.userId,
      fieldName: auditRequest.fieldName,
      reasonId: auditRequest.reasonId,
      comment: auditRequest.comment,
      accessedBy: auditRequest.accessedBy
    });
    
    // Use actual audit logging API - this will be intercepted by the enhanced HTTP interceptor
    const url = `${API_URLBLAZE}/audit/pii/log-access`;
    return this.http.post<PiiAuditResponse>(url, auditRequest).pipe(
      catchError(this.handleError<PiiAuditResponse>('logPiiAccess', { 
        success: false, 
        message: 'Audit logging failed' 
      }))
    );
  }

  /**
   * Get PII access history for a user
   * @param userId - The user ID
   * @param fieldName - Optional field name filter
   * @returns Observable<any[]>
   */
  getPiiAccessHistory(userId: string, fieldName?: string): Observable<any[]> {
    let url = `${API_URLBLAZE}/audit/pii/access-history/${userId}`;
    if (fieldName) {
      url += `?fieldName=${fieldName}`;
    }
    return this.http.get<any[]>(url).pipe(
      catchError(this.handleError<any[]>('getPiiAccessHistory', []))
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      // Log the error to remote logging infrastructure if needed
      // this.log(`${operation} failed: ${error.message}`);
      
      // Let the app keep running by returning an empty result
      return of(result as T);
    };
  }
} 